// SocraQuest push notification client.
// - Native (iOS/Android via Capacitor): @capacitor/push-notifications
// - Web: Firebase Cloud Messaging (config from /firebase-config.js → window.FIREBASE_CONFIG)
// Gracefully no-ops when config/permission/support is missing.
import { Capacitor } from '@capacitor/core';
import api from './api';

const DEVICE_KEY = 'push_registered_token';

async function saveTokenToBackend(fcmToken, platform) {
  try {
    // Avoid spamming the backend with the same token
    if (localStorage.getItem(DEVICE_KEY) === `${platform}:${fcmToken}`) return true;
  } catch {}
  await api.post('/api/notifications/register-device', { fcm_token: fcmToken, platform });
  try { localStorage.setItem(DEVICE_KEY, `${platform}:${fcmToken}`); } catch {}
  return true;
}

// ---------- Native (Capacitor) ----------
async function registerNativePush() {
  const { PushNotifications } = await import('@capacitor/push-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return { ok: false, reason: 'permission-denied' };

  return new Promise((resolve) => {
    PushNotifications.addListener('registration', async (token) => {
      try {
        const platform = Capacitor.getPlatform(); // 'ios' | 'android'
        await saveTokenToBackend(token.value, platform);
        resolve({ ok: true, token: token.value });
      } catch (e) {
        resolve({ ok: false, reason: 'backend-save-failed' });
      }
    });
    PushNotifications.addListener('registrationError', () => {
      resolve({ ok: false, reason: 'registration-error' });
    });
    PushNotifications.register();
  });
}

export async function attachNativePushListeners(onNotification) {
  if (!Capacitor.isNativePlatform()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  // Foreground notification received
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    if (onNotification) onNotification(notification);
  });
  // User tapped the notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = action?.notification?.data?.url;
    if (!url) return;
    // Links to our own site open the same screen inside the app (the Android app ships its own files).
    const local = String(url).replace(/^https?:\/\/(www\.)?socraquest\.sk/i, '') || '/';
    window.location.href = local.startsWith('/') ? local : url;
  });
}

// ---------- Web (Firebase JS SDK) ----------
function webPushConfigured() {
  const cfg = typeof window !== 'undefined' ? window.FIREBASE_CONFIG : null;
  return !!(cfg && cfg.apiKey && cfg.projectId && window.FIREBASE_VAPID_KEY);
}

export function webPushSupported() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && webPushConfigured();
}

async function registerWebPush(onForegroundMessage) {
  if (!webPushSupported()) return { ok: false, reason: 'not-configured' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'permission-denied' };

  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, getToken, onMessage, isSupported } = await import('firebase/messaging');

  if (!(await isSupported())) return { ok: false, reason: 'browser-unsupported' };

  const app = getApps().length ? getApps()[0] : initializeApp(window.FIREBASE_CONFIG);
  const messaging = getMessaging(app);

  const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(messaging, {
    vapidKey: window.FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: swReg,
  });
  if (!token) return { ok: false, reason: 'no-token' };

  await saveTokenToBackend(token, 'web');

  if (onForegroundMessage) {
    onMessage(messaging, (payload) => onForegroundMessage(payload));
  }
  return { ok: true, token };
}

// ---------- Public API ----------

/** Current permission state: 'granted' | 'denied' | 'prompt' | 'unsupported' */
export async function getPushPermissionState() {
  if (Capacitor.isNativePlatform()) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const p = await PushNotifications.checkPermissions();
      return p.receive === 'granted' ? 'granted' : p.receive === 'denied' ? 'denied' : 'prompt';
    } catch { return 'unsupported'; }
  }
  if (!webPushSupported()) return 'unsupported';
  return Notification.permission === 'default' ? 'prompt' : Notification.permission;
}

/**
 * Ask for permission (needs a user gesture on web) and register this device.
 * onForegroundMessage(payload) is called for messages received while the app is open.
 */
export async function enablePushNotifications(onForegroundMessage) {
  try {
    if (Capacitor.isNativePlatform()) {
      const res = await registerNativePush();
      if (res.ok) await attachNativePushListeners((n) => {
        if (onForegroundMessage) onForegroundMessage({ notification: { title: n.title, body: n.body }, data: n.data });
      });
      return res;
    }
    return await registerWebPush(onForegroundMessage);
  } catch (e) {
    console.warn('Push registration failed:', e);
    return { ok: false, reason: String(e).slice(0, 120) };
  }
}

/**
 * Silent re-registration on app start for devices that already granted permission
 * (keeps the FCM token fresh). Never prompts.
 */
export async function refreshPushRegistrationIfGranted(onForegroundMessage) {
  const state = await getPushPermissionState();
  if (state !== 'granted') return { ok: false, reason: 'not-granted' };
  return enablePushNotifications(onForegroundMessage);
}
